'use client';

import { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';
import { ErrorLogger } from '@/lib/error-logger';

interface WebVitals {
  lcp: number;
  fid: number;
  cls: number;
  ttfb: number;
  fcp: number;
  inp: number;
}

interface PageMetric {
  page: string;
  loadTime: number;
  ttfb: number;
  fcp: number;
  lcp: number;
  sessions: number;
  bounceRate: number;
}

interface ApiMetric {
  endpoint: string;
  avgResponse: number;
  p95Response: number;
  errorRate: number;
  calls: number;
  category: string;
}

interface TimeSeriesPoint {
  time: string;
  lcp: number;
  fid: number;
  ttfb: number;
  pageLoad: number;
}

const DEMO_VITALS: WebVitals = {
  lcp: 1840,
  fid: 42,
  cls: 0.08,
  ttfb: 320,
  fcp: 980,
  inp: 156,
};

const DEMO_PAGES: PageMetric[] = [
  { page: '/homepage', loadTime: 1240, ttfb: 280, fcp: 820, lcp: 1640, sessions: 8420, bounceRate: 32.4 },
  { page: '/flight-search', loadTime: 1840, ttfb: 340, fcp: 1020, lcp: 2140, sessions: 5280, bounceRate: 28.1 },
  { page: '/booking', loadTime: 1560, ttfb: 310, fcp: 940, lcp: 1820, sessions: 2140, bounceRate: 18.6 },
  { page: '/live-pnr-dashboard', loadTime: 2100, ttfb: 420, fcp: 1180, lcp: 2480, sessions: 1840, bounceRate: 22.3 },
  { page: '/itinerary-gallery', loadTime: 2340, ttfb: 380, fcp: 1240, lcp: 2820, sessions: 1620, bounceRate: 41.2 },
  { page: '/visa-requirements-matrix', loadTime: 1680, ttfb: 295, fcp: 880, lcp: 1940, sessions: 1240, bounceRate: 35.8 },
  { page: '/hotels', loadTime: 1920, ttfb: 360, fcp: 1080, lcp: 2240, sessions: 980, bounceRate: 29.4 },
  { page: '/analytics', loadTime: 2560, ttfb: 480, fcp: 1380, lcp: 3020, sessions: 420, bounceRate: 15.2 },
];

const DEMO_APIS: ApiMetric[] = [
  { endpoint: '/api/anthropic/travel-recommendations', avgResponse: 1240, p95Response: 2840, errorRate: 0.8, calls: 4820, category: 'AI' },
  { endpoint: '/api/anthropic/price-monitoring', avgResponse: 980, p95Response: 2120, errorRate: 0.4, calls: 3640, category: 'AI' },
  { endpoint: '/api/anthropic/visa-urgency-analysis', avgResponse: 1580, p95Response: 3240, errorRate: 1.2, calls: 1820, category: 'AI' },
  { endpoint: '/api/anthropic/chat', avgResponse: 2140, p95Response: 4820, errorRate: 0.6, calls: 8420, category: 'AI' },
  { endpoint: '/api/ai/chat-completion', avgResponse: 1840, p95Response: 3640, errorRate: 0.9, calls: 2840, category: 'AI' },
  { endpoint: '/api/anthropic/embassy-status-check', avgResponse: 840, p95Response: 1640, errorRate: 0.2, calls: 1240, category: 'AI' },
  { endpoint: '/api/anthropic/onward-ticket-analysis', avgResponse: 1120, p95Response: 2240, errorRate: 0.5, calls: 920, category: 'AI' },
  { endpoint: '/api/anthropic/price-alerts', avgResponse: 620, p95Response: 1240, errorRate: 0.1, calls: 3820, category: 'AI' },
];

const generateTimeSeries = (): TimeSeriesPoint[] => {
  const points: TimeSeriesPoint[] = [];
  const now = Date.now();
  for (let i = 23; i >= 0; i--) {
    const t = new Date(now - i * 3600000);
    points.push({
      time: t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      lcp: Math.round(1600 + Math.random() * 800),
      fid: Math.round(30 + Math.random() * 40),
      ttfb: Math.round(250 + Math.random() * 200),
      pageLoad: Math.round(1200 + Math.random() * 1000),
    });
  }
  return points;
};

const getVitalStatus = (metric: string, value: number): { label: string; color: string; bg: string; badge: string } => {
  const thresholds: Record<string, [number, number]> = {
    lcp: [2500, 4000],
    fid: [100, 300],
    cls: [0.1, 0.25],
    ttfb: [800, 1800],
    fcp: [1800, 3000],
    inp: [200, 500],
  };
  const [good, poor] = thresholds[metric] || [1000, 3000];
  if (value <= good) return { label: 'Good', color: 'text-green-600', bg: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700' };
  if (value <= poor) return { label: 'Needs Improvement', color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', badge: 'bg-yellow-100 text-yellow-700' };
  return { label: 'Poor', color: 'text-red-600', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700' };
};

const getLoadColor = (ms: number) => {
  if (ms < 1500) return '#22c55e';
  if (ms < 2500) return '#eab308';
  return '#ef4444';
};

const PerformanceAnalyticsInteractive = () => {
  const [activeTab, setActiveTab] = useState<'vitals' | 'pages' | 'api' | 'trends'>('vitals');
  const [vitals, setVitals] = useState<WebVitals>(DEMO_VITALS);
  const [timeSeries] = useState<TimeSeriesPoint[]>(generateTimeSeries());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('24h');

  const refreshData = useCallback(() => {
    setVitals({
      lcp: Math.round(1600 + Math.random() * 800),
      fid: Math.round(25 + Math.random() * 60),
      cls: Math.round((0.04 + Math.random() * 0.12) * 100) / 100,
      ttfb: Math.round(250 + Math.random() * 200),
      fcp: Math.round(800 + Math.random() * 600),
      inp: Math.round(100 + Math.random() * 150),
    });
    setLastUpdated(new Date());
  }, []);

  useEffect(() => {
    ErrorLogger.seedDemoData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshData, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshData]);

  const vitalsData = [
    { key: 'lcp', label: 'LCP', fullName: 'Largest Contentful Paint', value: vitals.lcp, unit: 'ms', description: 'Loading performance — time for largest content element to render' },
    { key: 'fid', label: 'FID', fullName: 'First Input Delay', value: vitals.fid, unit: 'ms', description: 'Interactivity — time from first user interaction to browser response' },
    { key: 'cls', label: 'CLS', fullName: 'Cumulative Layout Shift', value: vitals.cls, unit: '', description: 'Visual stability — unexpected layout shifts during page load' },
    { key: 'ttfb', label: 'TTFB', fullName: 'Time to First Byte', value: vitals.ttfb, unit: 'ms', description: 'Server response time — time until first byte received from server' },
    { key: 'fcp', label: 'FCP', fullName: 'First Contentful Paint', value: vitals.fcp, unit: 'ms', description: 'Perceived load speed — time until first content appears on screen' },
    { key: 'inp', label: 'INP', fullName: 'Interaction to Next Paint', value: vitals.inp, unit: 'ms', description: 'Overall responsiveness — latency of all page interactions' },
  ];

  const avgPageLoad = Math.round(DEMO_PAGES.reduce((s, p) => s + p.loadTime, 0) / DEMO_PAGES.length);
  const avgApiResponse = Math.round(DEMO_APIS.reduce((s, a) => s + a.avgResponse, 0) / DEMO_APIS.length);
  const goodVitals = vitalsData.filter(v => getVitalStatus(v.key, v.value).label === 'Good').length;

  return (
    <div className="min-h-screen bg-surface pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg">
                <Icon name="BoltIcon" size={20} className="text-white" />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground">Performance Analytics</h1>
            </div>
            <p className="text-muted-foreground">Core Web Vitals, page load times, API response times, and UX metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-surface border border-border rounded-lg overflow-hidden">
              {(['1h', '24h', '7d'] as const).map(range => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-3 py-2 text-xs font-bold transition-colors ${
                    timeRange === range ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <button
              onClick={() => setAutoRefresh(v => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                autoRefresh ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-muted-foreground border-border'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              {autoRefresh ? 'Live' : 'Paused'}
            </button>
            <button
              onClick={refreshData}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              <Icon name="ArrowPathIcon" size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Core Web Vitals</span>
              <Icon name="CheckBadgeIcon" size={16} className="text-green-500" />
            </div>
            <div className="text-3xl font-black text-foreground">{goodVitals}<span className="text-base font-normal text-muted-foreground">/{vitalsData.length}</span></div>
            <div className="text-xs text-muted-foreground mt-1">Passing good threshold</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Avg Page Load</span>
              <Icon name="ClockIcon" size={16} className="text-blue-500" />
            </div>
            <div className={`text-3xl font-black ${avgPageLoad < 2000 ? 'text-green-600' : avgPageLoad < 3000 ? 'text-yellow-600' : 'text-red-600'}`}>
              {(avgPageLoad / 1000).toFixed(1)}<span className="text-base font-normal">s</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Across {DEMO_PAGES.length} pages</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Avg API Response</span>
              <Icon name="ServerIcon" size={16} className="text-purple-500" />
            </div>
            <div className={`text-3xl font-black ${avgApiResponse < 1000 ? 'text-green-600' : avgApiResponse < 2000 ? 'text-yellow-600' : 'text-red-600'}`}>
              {avgApiResponse}<span className="text-base font-normal">ms</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{DEMO_APIS.length} endpoints tracked</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Last Updated</span>
              <Icon name="CalendarIcon" size={16} className="text-orange-500" />
            </div>
            <div className="text-lg font-black text-foreground">{lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
            <div className="text-xs text-muted-foreground mt-1">{lastUpdated.toLocaleDateString('en-GB')}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-border shadow-sm">
          <div className="flex border-b border-border overflow-x-auto">
            {(['vitals', 'pages', 'api', 'trends'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-5 py-4 font-bold text-sm transition-all whitespace-nowrap capitalize ${
                  activeTab === tab ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-surface'
                }`}
              >
                <Icon name={tab === 'vitals' ? 'HeartIcon' : tab === 'pages' ? 'DocumentTextIcon' : tab === 'api' ? 'ServerIcon' : 'PresentationChartLineIcon'} size={16} />
                {tab === 'vitals' ? 'Core Web Vitals' : tab === 'pages' ? 'Page Performance' : tab === 'api' ? 'API Metrics' : 'Trends'}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Core Web Vitals Tab */}
            {activeTab === 'vitals' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold text-foreground">Core Web Vitals</h3>
                  <a href="https://web.dev/vitals/" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                    <Icon name="ArrowTopRightOnSquareIcon" size={12} />
                    Learn about Web Vitals
                  </a>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vitalsData.map(vital => {
                    const status = getVitalStatus(vital.key, vital.value);
                    return (
                      <div key={vital.key} className={`rounded-xl border p-5 ${status.bg}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-heading text-2xl font-black text-foreground">{vital.label}</span>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${status.badge}`}>{status.label}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">{vital.fullName}</div>
                          </div>
                        </div>
                        <div className={`text-4xl font-black ${status.color}`}>
                          {vital.value}{vital.unit}
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{vital.description}</p>
                        <div className="mt-3 w-full bg-white/60 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              status.label === 'Good' ? 'bg-green-500' : status.label === 'Needs Improvement' ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: status.label === 'Good' ? '90%' : status.label === 'Needs Improvement' ? '55%' : '25%' }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="InformationCircleIcon" size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <strong>Thresholds:</strong> LCP &lt;2.5s (Good), FID &lt;100ms (Good), CLS &lt;0.1 (Good), TTFB &lt;800ms (Good), FCP &lt;1.8s (Good), INP &lt;200ms (Good). Metrics are collected from real user sessions and updated on refresh.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Page Performance Tab */}
            {activeTab === 'pages' && (
              <div className="space-y-6">
                <h3 className="font-heading text-lg font-bold text-foreground">Page Load Performance</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={DEMO_PAGES} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="page" tick={{ fontSize: 10 }} tickFormatter={v => v.replace('/', '').slice(0, 12)} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}ms`} />
                      <Tooltip formatter={(v: number) => [`${v}ms`, '']} labelFormatter={l => `Page: ${l}`} />
                      <Bar dataKey="loadTime" name="Load Time" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="lcp" name="LCP" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="ttfb" name="TTFB" fill="#a78bfa" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Page</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Load Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">TTFB</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">FCP</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">LCP</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sessions</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bounce Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {DEMO_PAGES.map((page, i) => (
                        <tr key={i} className="hover:bg-surface/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-foreground">{page.page}</td>
                          <td className="px-4 py-3">
                            <span className="font-bold" style={{ color: getLoadColor(page.loadTime) }}>{page.loadTime}ms</span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{page.ttfb}ms</td>
                          <td className="px-4 py-3 text-muted-foreground">{page.fcp}ms</td>
                          <td className="px-4 py-3">
                            <span className="font-bold" style={{ color: getLoadColor(page.lcp) }}>{page.lcp}ms</span>
                          </td>
                          <td className="px-4 py-3 font-bold text-foreground">{page.sessions.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`font-bold ${
                              page.bounceRate < 30 ? 'text-green-600' : page.bounceRate < 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>{page.bounceRate}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* API Metrics Tab */}
            {activeTab === 'api' && (
              <div className="space-y-6">
                <h3 className="font-heading text-lg font-bold text-foreground">API Response Times</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={DEMO_APIS} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `${v}ms`} />
                      <YAxis type="category" dataKey="endpoint" tick={{ fontSize: 9 }} width={180} tickFormatter={v => v.replace('/api/anthropic/', '').replace('/api/ai/', '')} />
                      <Tooltip formatter={(v: number) => [`${v}ms`, '']} />
                      <Bar dataKey="avgResponse" name="Avg Response" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="p95Response" name="P95 Response" fill="#c4b5fd" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Endpoint</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Avg Response</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">P95 Response</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Error Rate</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total Calls</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {DEMO_APIS.map((api, i) => (
                        <tr key={i} className="hover:bg-surface/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-foreground max-w-[200px] truncate">{api.endpoint}</td>
                          <td className="px-4 py-3">
                            <span className={`font-bold ${
                              api.avgResponse < 1000 ? 'text-green-600' : api.avgResponse < 2000 ? 'text-yellow-600' : 'text-red-600'
                            }`}>{api.avgResponse}ms</span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{api.p95Response}ms</td>
                          <td className="px-4 py-3">
                            <span className={`font-bold ${
                              api.errorRate < 1 ? 'text-green-600' : api.errorRate < 5 ? 'text-yellow-600' : 'text-red-600'
                            }`}>{api.errorRate}%</span>
                          </td>
                          <td className="px-4 py-3 font-bold text-foreground">{api.calls.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                              api.avgResponse < 1000 ? 'bg-green-100 text-green-700' : api.avgResponse < 2000 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {api.avgResponse < 1000 ? 'Fast' : api.avgResponse < 2000 ? 'Moderate' : 'Slow'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Trends Tab */}
            {activeTab === 'trends' && (
              <div className="space-y-6">
                <h3 className="font-heading text-lg font-bold text-foreground">Performance Trends (24h)</h3>
                <div>
                  <h4 className="text-sm font-bold text-muted-foreground mb-3">Page Load Time & LCP</h4>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={timeSeries} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="pageLoadGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="lcpGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={3} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}ms`} />
                        <Tooltip formatter={(v: number) => [`${v}ms`, '']} />
                        <Area type="monotone" dataKey="pageLoad" name="Page Load" stroke="#6366f1" fill="url(#pageLoadGrad)" strokeWidth={2} />
                        <Area type="monotone" dataKey="lcp" name="LCP" stroke="#8b5cf6" fill="url(#lcpGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-muted-foreground mb-3">TTFB & FID</h4>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timeSeries} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={3} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}ms`} />
                        <Tooltip formatter={(v: number) => [`${v}ms`, '']} />
                        <Line type="monotone" dataKey="ttfb" name="TTFB" stroke="#f59e0b" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="fid" name="FID" stroke="#10b981" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Best Page Load', value: `${Math.min(...DEMO_PAGES.map(p => p.loadTime))}ms`, page: DEMO_PAGES.reduce((a, b) => a.loadTime < b.loadTime ? a : b).page, color: 'text-green-600' },
                    { label: 'Slowest Page', value: `${Math.max(...DEMO_PAGES.map(p => p.loadTime))}ms`, page: DEMO_PAGES.reduce((a, b) => a.loadTime > b.loadTime ? a : b).page, color: 'text-red-600' },
                    { label: 'Fastest API', value: `${Math.min(...DEMO_APIS.map(a => a.avgResponse))}ms`, page: DEMO_APIS.reduce((a, b) => a.avgResponse < b.avgResponse ? a : b).endpoint.split('/').pop() || '', color: 'text-green-600' },
                  ].map((item, i) => (
                    <div key={i} className="bg-surface rounded-xl border border-border p-4">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{item.label}</div>
                      <div className={`text-2xl font-black ${item.color}`}>{item.value}</div>
                      <div className="text-xs text-muted-foreground mt-1 font-mono truncate">{item.page}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalyticsInteractive;
